import { useState, useEffect } from "react";
import { 
  getAllFinances, 
  addTransaction, 
  alertPartner, 
  removeAlert, 
  blockPartner, 
  unblockPartner,
  getPricing,
  savePricing,
  PartnerFinance,
  TransactionType,
  PricingConfig
} from "@/data/financeStore";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { 
  Wallet, AlertTriangle, CheckCircle, Ban, History, DollarSign, HandCoins, Settings, ShieldAlert
} from "lucide-react";
import { Label } from "@/components/ui/label";

export default function AdminFinancePage() {
  const { user, isAuthenticated } = useAuth();
  const [finances, setFinances] = useState<PartnerFinance[]>([]);
  const [pricing, setPricing] = useState<PricingConfig>(getPricing());
  
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  
  const [isRechargeOpen, setIsRechargeOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  useEffect(() => {
    setFinances(getAllFinances());
    setPricing(getPricing());
  }, []);

  if (!isAuthenticated || user?.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleAlert = (partnerId: string, currentStatus: boolean) => {
    if (currentStatus) {
      removeAlert(partnerId);
    } else {
      alertPartner(partnerId);
    }
    setFinances(getAllFinances());
  };

  const handleBlock = (partnerId: string, currentStatus: boolean = false) => {
    if (currentStatus) {
      unblockPartner(partnerId);
    } else {
      blockPartner(partnerId);
    }
    setFinances(getAllFinances());
  };

  const handleRecharge = () => {
    if (!selectedPartner || !amount || parseFloat(amount) <= 0) return;
    addTransaction(selectedPartner, "RECHARGE", parseFloat(amount), description || "Recharge manuelle (Admin)");
    setFinances(getAllFinances());
    setIsRechargeOpen(false);
    setAmount("");
    setDescription("");
  };

  const handleSavePricing = () => {
    savePricing(pricing);
    setIsPricingOpen(false);
  };

  const totalBalance = finances.reduce((acc, curr) => acc + curr.balance, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Gestion Financière</h1>
          <p className="text-muted-foreground font-body">Supervisez les soldes, ajoutez des recharges et gérez la tarification.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsPricingOpen(true)} variant="outline" className="gap-2 font-display">
            <Settings className="w-4 h-4" /> Tarification
          </Button>
          <Button onClick={() => setIsRechargeOpen(true)} className="gap-2 font-display">
            <HandCoins className="w-4 h-4" /> Nouvelle Recharge
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-6">
        <Card className="bg-primary/5 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-foreground/70 font-display flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" /> Solde Global de la Plateforme (Dettes/Crédits)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-display ${totalBalance < 0 ? 'text-destructive' : 'text-primary'}`}>
              {totalBalance} DHS
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-display font-bold text-foreground">Comptes Partis & ONGs</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left font-body text-sm">
            <thead className="bg-muted text-muted-foreground border-b border-border">
              <tr>
                <th className="p-4 font-semibold">Partenaire</th>
                <th className="p-4 font-semibold">Type</th>
                <th className="p-4 font-semibold">Solde (DHS)</th>
                <th className="p-4 font-semibold">Statut</th>
                <th className="p-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {finances.map((f) => (
                <tr key={f.partnerId} className="hover:bg-muted/30 transition-colors">
                  <td className="p-4">
                    <p className="font-bold text-foreground">{f.partnerName}</p>
                    <p className="text-xs text-muted-foreground">{f.partnerId}</p>
                  </td>
                  <td className="p-4">
                    <Badge variant="outline">{f.partnerType}</Badge>
                  </td>
                  <td className="p-4">
                    <span className={`font-bold ${f.balance < 0 ? 'text-destructive' : 'text-emerald-600'}`}>
                      {f.balance}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2 flex-wrap">
                      {f.isBlocked ? (
                        <Badge variant="destructive" className="flex gap-1 items-center">
                          <Ban className="w-3 h-3" /> Bloqué
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="flex gap-1 items-center bg-emerald-100 text-emerald-800">
                          <CheckCircle className="w-3 h-3" /> Actif
                        </Badge>
                      )}
                      
                      {f.hasAlert && (
                        <Badge variant="outline" className="border-orange-500 text-orange-600 bg-orange-50 flex gap-1 items-center">
                          <AlertTriangle className="w-3 h-3" /> À relancer
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleAlert(f.partnerId, f.hasAlert)}
                      className={f.hasAlert ? "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100" : ""}
                    >
                      <ShieldAlert className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleBlock(f.partnerId, f.isBlocked)}
                      className={f.isBlocked ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100" : "text-destructive hover:text-destructive"}
                    >
                      <Ban className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {finances.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">Aucun compte financier trouvé.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Recharge */}
      <Dialog open={isRechargeOpen} onOpenChange={setIsRechargeOpen}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle className="font-display">Nouvelle Recharge Manuelle</DialogTitle>
            <DialogDescription>
              Ajouter des crédits au compte d'un parti ou d'une ONG.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Partenaire</Label>
              <select 
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                value={selectedPartner || ""}
                onChange={(e) => setSelectedPartner(e.target.value)}
              >
                <option value="">Sélectionner...</option>
                {finances.map(f => (
                  <option key={f.partnerId} value={f.partnerId}>{f.partnerName}</option>
                ))}
              </select>
            </div>
            <div className="grid gap-2">
              <Label>Montant (DHS)</Label>
              <Input 
                type="number" 
                placeholder="Ex: 1000" 
                value={amount} 
                onChange={(e) => setAmount(e.target.value)} 
              />
            </div>
            <div className="grid gap-2">
              <Label>Description / Motif</Label>
              <Input 
                placeholder="Virement bancaire N°..." 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRechargeOpen(false)}>Annuler</Button>
            <Button onClick={handleRecharge} disabled={!selectedPartner || !amount}>Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Tarification */}
      <Dialog open={isPricingOpen} onOpenChange={setIsPricingOpen}>
        <DialogContent className="sm:max-w-lg bg-card">
          <DialogHeader>
            <DialogTitle className="font-display">Tarification de la plateforme (DHS)</DialogTitle>
            <DialogDescription>
              Modifiez les prix appliqués aux différents types de partenaires.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            
            <div className="space-y-3">
              <h4 className="font-bold border-b pb-1">Abonnement Annuel</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">PARTI</Label>
                  <Input type="number" value={pricing.abonnement.PARTI} onChange={e => setPricing({...pricing, abonnement: {...pricing.abonnement, PARTI: Number(e.target.value)}})} />
                </div>
                <div>
                  <Label className="text-xs">ONG</Label>
                  <Input type="number" value={pricing.abonnement.ONG} onChange={e => setPricing({...pricing, abonnement: {...pricing.abonnement, ONG: Number(e.target.value)}})} />
                </div>
                <div>
                  <Label className="text-xs">INSTITUTION</Label>
                  <Input type="number" value={pricing.abonnement.INSTITUTION} onChange={e => setPricing({...pricing, abonnement: {...pricing.abonnement, INSTITUTION: Number(e.target.value)}})} />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold border-b pb-1">Coût par Publicité</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">PARTI</Label>
                  <Input type="number" value={pricing.pub.PARTI} onChange={e => setPricing({...pricing, pub: {...pricing.pub, PARTI: Number(e.target.value)}})} />
                </div>
                <div>
                  <Label className="text-xs">ONG</Label>
                  <Input type="number" value={pricing.pub.ONG} onChange={e => setPricing({...pricing, pub: {...pricing.pub, ONG: Number(e.target.value)}})} />
                </div>
                <div>
                  <Label className="text-xs">INSTITUTION</Label>
                  <Input type="number" value={pricing.pub.INSTITUTION} onChange={e => setPricing({...pricing, pub: {...pricing.pub, INSTITUTION: Number(e.target.value)}})} />
                </div>
              </div>
            </div>

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPricingOpen(false)}>Annuler</Button>
            <Button onClick={handleSavePricing}>Enregistrer les tarifs</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
